import { Box, Grid2 as Grid, Stack, TextField, Typography, useMediaQuery, useTheme } from "@mui/material"
import { useMutation } from "@tanstack/react-query"
import { useEffect, useRef, useState } from "react"
import { API } from "../../api/API"
import { useFindEmployeeListByProject } from "../../api/EmployeeApi"
import { DatePickerCustom } from "../../components/Fields/DatePickerCustom"
import { EmployeeField } from "../../components/Fields/EmployeeField"
import { MultipleFlightPlanSelector } from "../../components/MultipleFlightPlanSelector/MultipleFlightPlanSelector"
import { StepperForm } from "../../components/StepperForm/StepperForm"
import { MissionTitle } from "../../components/Titles/MissionTitle"
import { ProjectTitle } from "../../components/Titles/ProjectTitle"
import { useNexusNavigate } from "../../hooks/useNexusNavigate"
import { useNexusMissionId, useNexusProjectNumber } from "../../hooks/useNexusParams"
import { useTitle } from "../../hooks/useTitle"
import { ReviewedGeometriesMap } from "../../mapping/ReviewedGeometriesMap"
import { FlightlineEventReview, MissionReviewPutDTO, geometryQCStatusProperties } from "../../models/MissionReview"
import { Order, SortOrder } from "../../models/SortOrder"
import { ConfirmationRoutes } from "../../routing/NexRoutes"
import { ToolbarOptions } from "../../store/types/AuthTypes"
import { isEmptyOrInvalid } from "../../tools"
import { GeometryReviewStatus, GeometryWithStatus } from "../../utils/GeoJSONUtils"
import { MissionReviewUtils, reviewTableLabel } from "../../utils/MissionReviewUtils"
import { StringUtils } from "../../utils/StringUtils"
import { TimeUtils } from "../../utils/TimeUtils"
import { useAppErrorStore } from "../../zstore/AppErrorStore"
import { useAuthStore } from "../../zstore/AuthStore"
import { useLocalStorageStore } from "../../zstore/LocalStorageStore"
import { useMissionReviewStore } from "../../zstore/MissionReviewStore"
import { MissionReviewTableAndDrawer } from "./MissionReviewTableAndDrawer"

export const MissionReviewPage = () => {
    useTitle("Mission Review")
    const nexusNav = useNexusNavigate()
    const missionId = useNexusMissionId()
    const projectNumber = useNexusProjectNumber()
    const checkToolbarAccess = useAuthStore((state) => state.checkToolbarAccess)
    const tableSortOrders = useLocalStorageStore((state) => state.tableSortOrders)
    const reviewSortOrder = (tableSortOrders[reviewTableLabel] as SortOrder<keyof FlightlineEventReview>) ?? {
        orderBy: "flightplan_name_short_label",
        order: Order.desc,
    }
    const disableReviewFields = !checkToolbarAccess(ToolbarOptions.Flightline_QC_Review)
    const mapHeightRef = useRef<HTMLDivElement | null>(null)
    const [mapHeight, setMapHeight] = useState<number | undefined>(undefined)

    const [warnings, setWarnings] = useState<string[]>([])

    const selectedIds = useMissionReviewStore((state) => state.selectedEventIds)
    const missionName = useMissionReviewStore((state) => state.missionName)
    const flightPlans = useMissionReviewStore((state) => state.flightPlans)
    const missionReview = useMissionReviewStore((state) => state.missionReview)
    const setMissionReview = useMissionReviewStore((state) => state.setMissionReview)
    const eventReviews = useMissionReviewStore((state) => state.flightLineReviews)
    const resetReview = useMissionReviewStore((state) => state.resetReview)
    const fetchMissionReview = useMissionReviewStore((state) => state.fetchMissionReview)
    const missionReviewIsFetching = useMissionReviewStore((state) => state.isFetching)
    const missionReviewError = useMissionReviewStore((state) => state.error)

    const theme = useTheme()
    const largeScreen = useMediaQuery(theme.breakpoints.up("lg"))

    const employeeResponse = useFindEmployeeListByProject(projectNumber)
    const employeeList = employeeResponse.data

    const loading = missionReviewIsFetching || employeeResponse.isLoading

    useEffect(() => {
        fetchMissionReview(missionId, reviewSortOrder)
        return resetReview
    }, [])

    useEffect(() => {
        if (largeScreen) {
            // For large screens where it is two columns, use the left column height
            setMapHeight(mapHeightRef.current?.clientHeight ?? undefined)
        } else {
            // For medium and smaller screens, use a set height
            setMapHeight(400)
        }
    }, [mapHeightRef.current?.clientHeight])

    const geoJsonLines: GeometryWithStatus[] =
        eventReviews
            ?.filter((eventReview) => eventReview.shape_geojson != null)
            ?.map((eventReview) => ({
                selected: selectedIds.includes(eventReview.fe_review_id),
                geometry: eventReview.shape_geojson!,
                status:
                    eventReview.qc_status == null
                        ? GeometryReviewStatus.UNDER_REVIEW
                        : geometryQCStatusProperties(eventReview.qc_status).relatedStatus,
            })) ?? []

    const handleSaveMissionReview = useMutation({
        mutationFn: async (payload: MissionReviewPutDTO) => {
            return await API.put(`/mission-review/${missionId}`, payload, "Couldn't save mission review.")
        },
        onSuccess: () => {
            nexusNav.confirmationPage(ConfirmationRoutes.MISSION_REVIEWED, {
                entityName: missionName,
                projectNumber: projectNumber,
            })
        },
        onError: (error) => {
            useAppErrorStore.getState().setAppError(error.message)
        },
    })

    const handleStepChange = async (newStep: number) => {
        if (newStep === -1) {
            // go back to mission selection page
            nexusNav.missionSelect(projectNumber)
        } else {
            if (
                MissionReviewUtils.checkValidMissionReview(missionReview) &&
                MissionReviewUtils.checkValidEventReviews(eventReviews)
            ) {
                const payload: MissionReviewPutDTO = {
                    mission_id: +missionId,
                    review_date: missionReview.review_date,
                    reviewer_id: missionReview.reviewer_id,
                    reviewer_notes: missionReview.reviewer_notes,
                    flightline_event_reviews: MissionReviewUtils.transformEventReviewToReviewDTO(eventReviews),
                }

                handleSaveMissionReview.mutate(payload)
                return
            }

            setWarnings(["Cannot save review: Missing values."])
            return
        }
    }

    return (
        <StepperForm
            header={
                <>
                    <ProjectTitle projectNumber={projectNumber} />
                    <MissionTitle loading={loading} missionName={missionName} />
                </>
            }
            onStepChange={handleStepChange}
            loading={loading}
            submitting={handleSaveMissionReview.isPending}
            warnings={StringUtils.parseWarningsFromErrors(missionReviewError, ...warnings)}
            disableSubmit={
                disableReviewFields ||
                MissionReviewUtils.checkValidMissionReview(missionReview) === false ||
                MissionReviewUtils.checkValidEventReviews(eventReviews) === false
            }
        >
            <Grid container={true} spacing={2}>
                <Grid size={{ xs: 12, lg: 6 }}>
                    <Stack minWidth={400} ref={mapHeightRef} gap={2}>
                        <Typography variant={"h6"}>Mission Review</Typography>
                        <DatePickerCustom
                            label="Review Date"
                            value={missionReview?.review_date ? TimeUtils.parseDate(missionReview.review_date) : null}
                            onChange={(date: moment.Moment | null) => {
                                const formattedDate = TimeUtils.formatDate(date)
                                setMissionReview({
                                    review_date: isEmptyOrInvalid(formattedDate) ? undefined : formattedDate,
                                })
                            }}
                            required={true}
                        />
                        {employeeList && (
                            <EmployeeField
                                label="Reviewer"
                                employeeId={missionReview?.reviewer_id}
                                employeeRole="reviewers"
                                employeeList={employeeList}
                                handleChange={(employeeId) => setMissionReview({ reviewer_id: employeeId })}
                                required={true}
                                fullWidth={true}
                            />
                        )}
                        <TextField
                            label="Notes"
                            value={missionReview?.reviewer_notes ?? ""}
                            onChange={(e) => setMissionReview({ reviewer_notes: e.target.value })}
                            onBlur={(e) => setMissionReview({ reviewer_notes: e.target.value.trim() })}
                            multiline
                            minRows={4}
                            variant="outlined"
                            fullWidth
                        />

                        {flightPlans && (
                            <>
                                <Typography variant={"h6"}>Flight Plans</Typography>
                                <MultipleFlightPlanSelector selectedPlans={flightPlans} />
                            </>
                        )}
                    </Stack>
                </Grid>
                <Grid size={{ xs: 12, lg: 6 }}>
                    {mapHeight != null && (
                        <ReviewedGeometriesMap
                            // If the number of eventReviews changes then it doesn't automatically cause the map to
                            // rerender the amount of lines - causing style changes to effect neighboring lines.
                            key={JSON.stringify(eventReviews)}
                            geometries={geoJsonLines}
                            height={mapHeight > 400 ? mapHeight : 400}
                        />
                    )}
                </Grid>
            </Grid>
            <Box mt={2}>
                <MissionReviewTableAndDrawer />
            </Box>
        </StepperForm>
    )
}
